<template>
  <div>
    <div v-if="!loaded" class="preloader-overlay">
      <div class="preloader-card">
        <f7-preloader></f7-preloader>
        <div class="preloader-text">
          {{ $t('loading') }}
        </div>
      </div>
    </div>
    <div class="filterButtonPosition" style="top: 225px; right: 80px">
      <div
        style="
          display: flex;
          justify-content: right;
          align-items: center;
        "
      >
        <div>
          <a
            id="analysis-risk-info"
            class="analysis-risk-info"
            @mouseenter="toggleWithAnimationOptions"
            @mouseleave="toggleWithAnimationOptions"
          >
            <i class="f7-icons f7-icon-custom">info_circle_fill</i>
          </a>
        </div>
      </div>
    </div>
    <DxDataGrid
      :data-source="data"
      :show-borders="false"
      :show-column-lines="false"
      :allow-column-reordering="true"
      :allow-column-resizing="true"
      :column-auto-width="true"
      :show-column-headers="true"
      :show-colon="false"
      :remote-operations="true"
      :hover-state-enabled="false"
      :show-row-lines="false"
      :row-alternation-enabled="false"
      :no-data-text="$t('noDataText')"
      class="custom-grid custom-grid-detail-RiskAnalysis"
      @toolbar-preparing="onToolbarPreparing($event)"
      @exporting="onExporting"
    >
      <DxScrolling mode="standard" />

      <DxStateStoring
        :enabled="true"
        type="localStorage"
        storage-key="riskAnalysisTable"
      />

      <DxPaging :page-size="20" />
      <DxPager
        :show-page-size-selector="true"
        :allowed-page-sizes="[5, 10, 20]"
        :show-info="true"
        :visible="true"
        info-text="{2} items"
      />
      <DxSearchPanel
        :visible="true"
        :width="300"
        :placeholder="$t('search')"
      />

      <DxExport :enabled="true" />

      <DxGrouping />
      <DxGroupPanel :visible="false" />

      <DxColumn
        :caption="
          selectedSampleTypes !== null
            ? `${$t('agronomySample.summary.sampleType')}: ${
                selectedSampleTypes.name
              }`
            : $t('agronomySample.summary.sampleType')
        "
      >
        <DxColumn
          :name="data.Parametro"
          :caption="$t('foodSamples.parameter')"
          data-field="Parametro"
        />
      </DxColumn>

      <DxColumn
        :caption="$t('originCountry')"
        alignment="center"
        data-field="originCountryMain"
      >
        <DxColumn
          :caption="getOriginCountryTranslated"
          alignment="center"
          cell-template="templateCountry"
          data-field="originCountry"
        />
      </DxColumn>

      <DxColumn
        :caption="$t('destinationCountry')"
        alignment="center"
        data-field="destinationCountryMain"
      >
        <DxColumn
          v-for="columnData in selectedDestinationCountries"
          :key="columnData"
          :caption="getDestinationCountryTranslated(columnData)"
          alignment="center"
          cell-template="templateDestination"
          data-field="destinationCountry"
        />
      </DxColumn>

      <template #templateCountry="{ data }">
        <div style="text-align: center">
          <span>
            {{
              data.data[
                `lmr${
                  selectedOriginCountry != null
                    ? selectedOriginCountry.code
                    : userProfile.userCountry
                }`
              ]
            }}
          </span>
        </div>
      </template>

      <template #params="{ data }">
        <div>
          {{ data.data.Parametro }}
        </div>
      </template>

      <template #templateDestination="{ data }">
        <div style="display: flex; justify-content: center">
          <div
            :class="{
              lowRisk:
                +data.data[`lmr${getOriginCountry}`] <
                +data.data[`lmr${data.column.caption}`],
              avgRisk:
                +data.data[`lmr${getOriginCountry}`] ==
                +data.data[`lmr${data.column.caption}`],
              highRisk:
                +data.data[`lmr${getOriginCountry}`] >
                +data.data[`lmr${data.column.caption}`],
            }"
          >
            {{ data.data[`lmr${data.column.caption}`] }}
          </div>
        </div>
      </template>
      <template #inlineFiltersTemplate>
        <RiskAnalysisInlineFilters />
      </template>
    </DxDataGrid>
    <DxPopover
      :width="200"
      :visible="withAnimationOptionsVisible"
      :animation="animationConfig"
      target="#analysis-risk-info"
      position="bottom"
    >
      <div style="padding: 25px 20px 15px">
        <f7-row>
          <f7-col>
            <div
              style="height: 20px; margin-bottom: 10px"
              class="highRisk"
            ></div>
          </f7-col>
          <f7-col> {{ $t('highRisk') }} </f7-col>
        </f7-row>

        <f7-row>
          <f7-col>
            <div
              style="height: 20px; margin-bottom: 10px"
              class="avgRisk"
            ></div>
          </f7-col>
          <f7-col> {{ $t('avgRisk') }} </f7-col>
        </f7-row>

        <f7-row>
          <f7-col>
            <div
              style="height: 20px; margin-bottom: 10px"
              class="lowRisk"
            ></div>
          </f7-col>
          <f7-col> {{ $t('lowRisk') }} </f7-col>
        </f7-row>
      </div>
    </DxPopover>
  </div>
</template>
<script>
import { mapGetters, mapState, mapActions } from 'vuex';
import {
  DxDataGrid,
  DxColumn,
  DxSearchPanel,
  DxStateStoring,
  DxPager,
  DxPaging,
  DxGrouping,
  DxGroupPanel,
  DxScrolling,
  DxExport,
} from 'devextreme-vue/data-grid';

import saveAs from 'file-saver';
import { exportDataGrid } from 'devextreme/excel_exporter';
import { Workbook } from 'exceljs';
import { DxPopover } from 'devextreme-vue/popover';
import countryTranslationServiceEs from '../../../i18n/translated_countries_service_es';
import RiskAnalysisInlineFilters from './RiskAnalysisInlineFilters.vue';
import { ATCS_ES } from '../../../js/constants';

export default {
  components: {
    RiskAnalysisInlineFilters,
    DxDataGrid,
    DxColumn,
    DxSearchPanel,
    DxStateStoring,
    DxPager,
    DxPaging,
    DxScrolling,
    DxExport,
    DxGrouping,
    DxGroupPanel,
    DxPopover,
  },
  props: {
    data: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      editButtons: [
        {
          icon: 'find',
          onClick: this.viewAtc,
        },
        {
          icon: 'download',
          onClick: this.triggerContextualMenu,
        },
      ],
      withAnimationOptionsVisible: false,
      animationConfig: {
        show: {
          type: 'pop',
          from: {
            scale: 0,
          },
          to: {
            scale: 1,
          },
        },
        hide: {
          type: 'fade',
          from: 1,
          to: 0,
        },
      },
      loaded: false,
    };
  },
  computed: {
    getOriginCountryTranslated() {
      let code = '';

      if (this.enabled) {
        code =
          this.selectedOriginCountry != null
            ? this.selectedOriginCountry.code
            : this.userProfile.userCountry;
      } else {
        code =
          this.selectedOriginCountry !== null
            ? this.selectedOriginCountry.code
            : this.$t('originCountry');
      }

      if (code === 'UE') {
        return countryTranslationServiceEs.getCountryNameByCode(
          this.selectedEuropeanOriginCountry
        );
      }

      return countryTranslationServiceEs.getCountryNameByCode(code);
    },

    getOriginCountry() {
      return this.selectedOriginCountry != null
        ? this.selectedOriginCountry.code
        : this.userProfile.userCountry;
    },
    ...mapState('riskAnalysisFilter', [
      'selectedSampleTypes',
      'selectedDestinationCountries',
      'selectedOriginCountry',
      'selectedEuropeanOriginCountry',
    ]),
    ...mapState('riskAnalysis', [
      'sampleTypes',
      'originCountries',
      'destinationCountries',
    ]),
    ...mapGetters('user', ['userProfile']),
    ...mapState('contact', ['enabled']),
  },
  async beforeMount() {
    this.loaded = false;
    this.setCurrentFilter('riskAnalysis');
    await this.getSampleTypesSat({
      atc_name: ATCS_ES.food,
    });
    await this.getDestinationCountries();
    this.loaded = true;
  },
  methods: {
    getDestinationCountryTranslated(code) {
      return countryTranslationServiceEs.getCountryNameByCode(code);
    },
    toggleWithAnimationOptions() {
      this.withAnimationOptionsVisible =
        !this.withAnimationOptionsVisible;
    },
    onExporting(e) {
      e.component.beginUpdate();
      e.component.columnOption('ID', 'visible', true);
      const workbook = new Workbook();
      const worksheet = workbook.addWorksheet(
        `${`${this.$t(
          'environmentSamples.exportExcelSampleTable'
        )}-${this.$t('Food')}`}`
      );

      exportDataGrid({
        component: e.component,
        worksheet,
        customizeCell: ({ gridCell, excelCell }) => {
          if (gridCell.rowType === 'data') {
            if (
              gridCell.column.dataField === 'originCountry' ||
              gridCell.column.dataField === 'originCountryMain'
            ) {
              excelCell.value =
                gridCell.data[
                  `lmr${
                    this.selectedOriginCountry != null
                      ? this.selectedOriginCountry.code
                      : this.userProfile.userCountry
                  }`
                ];
            }
            if (
              gridCell.column.dataField === 'destinationCountry' ||
              gridCell.column.dataField === 'destinationCountryMain'
            ) {
              const lowRisk =
                +gridCell.data[`lmr${this.getOriginCountry}`] <
                +gridCell.data[`lmr${gridCell.column.caption}`];

              const avgRisk =
                +gridCell.data[`lmr${this.getOriginCountry}`] ==
                +gridCell.data[`lmr${gridCell.column.caption}`];

              const highRisk =
                +gridCell.data[`lmr${this.getOriginCountry}`] >
                +gridCell.data[`lmr${gridCell.column.caption}`];

              if (highRisk) {
                excelCell.fill = {
                  type: 'pattern',
                  pattern: 'solid',
                  fgColor: { argb: 'FCE8E8' },
                };
              }

              if (avgRisk) {
                excelCell.fill = {
                  type: 'pattern',
                  pattern: 'solid',
                  fgColor: { argb: 'f6d200' },
                };
              }

              if (lowRisk) {
                excelCell.fill = {
                  type: 'pattern',
                  pattern: 'solid',
                  fgColor: { argb: '00c56a' },
                };
              }

              excelCell.value =
                gridCell.data[`lmr${gridCell.column.caption}`];
            }
          }
        },
      })
        .then(() => {
          workbook.xlsx.writeBuffer().then((buffer) => {
            saveAs(
              new Blob([buffer], {
                type: 'application/octet-stream',
              }),
              `${`${this.$t(
                'environmentSamples.exportExcelSampleTable'
              )}-${this.$t('Food')}`}.xlsx`
            );
          });
        })
        .then(() => {
          e.component.columnOption('ID', 'visible', false);
          e.component.endUpdate();
        });

      e.cancel = true;
    },
    onToolbarPreparing(e) {
      const toolbarItems = e.toolbarOptions.items;
      e.toolbarOptions.elementAttr = {
        class: 'custom-simple-toolbar',
      };

      // FILTROS INLINE A LA IZQUIERDA
      toolbarItems.unshift({
        location: 'before',
        template: 'inlineFiltersTemplate',
      });

      toolbarItems.forEach((item) => {
        if (item.name === 'searchPanel') {
          item.location = 'after';
          item.elementAttr = {
            class: 'risk-analysis-search-panel',
          };
        }
        if (item.name === 'exportButton') {
          item.location = 'after';
          item.elementAttr = {
            class: 'risk-analysis-export-button',
          };
        }

        // if (item.name === 'groupPanel') {
        //   item.location = 'after';
        // }
      });
      // // Añadir el selector de agrupaciones
      // toolbarItems.push({
      //   name: 'Filtros',
      //   location: 'after',
      //   widget: 'dxButton',
      //   locateInMenu: 'never',
      //   options: {
      //     text: '',
      //     icon: '././static/icons/ic_filter.svg',
      //     width: 38,
      //     elementAttr: {
      //       class: 'btn-filtrar',
      //     },
      //     onClick: () => {
      //       this.$f7.panel.open('right');
      //     },
      //   },
      // });
    },
    ...mapActions('filter', ['setCurrentFilter']),
    ...mapActions('riskAnalysis', [
      'getSampleTypesSat',
      'getDestinationCountries',
    ]),
  },
};
</script>

<style>
.f7-icon-custom {
  font-size: 20px !important;
  height: 25px !important;
  width: 40px !important;
  /* border: 1px solid #00346a !important; */
  border-radius: 10px !important;
  background-color: #fff !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  padding: 0 !important;
  margin: 0 !important;
  box-sizing: border-box;
}
/* Dejar espacio para el icono con leyenda de riesgos a la derecha */
/* .custom-grid-detail-RiskAnalysis .dx-toolbar-after {
  margin-right: 75px;
} */
/* .custom-grid-detail-RiskAnalysis .dx-toolbar-after {
  display: grid;
  grid-template-columns: 120px 250px;
  grid-template-rows: repeat(1, 1fr);
  row-gap: 24px;
  margin-bottom: 12px;
  align-items: center;
  box-sizing: border-box;
}
.custom-grid-detail-RiskAnalysis .dx-toolbar-after :nth-child(1) {
  grid-row: 1;
  grid-column: 4;
  justify-self: center;
}
.custom-grid-detail-RiskAnalysis .dx-toolbar-after :nth-child(2) {
  grid-row: 1;
  grid-column: 1 / span 2;
  justify-self: start;
}
.custom-grid-detail-RiskAnalysis .dx-toolbar-after :nth-child(3) {
  grid-row: 1;
  grid-column: 3;
  justify-self: start;
} */
/* Base */
.custom-grid-detail-RiskAnalysis
  .dx-datagrid-headers
  .dx-datagrid-table
  .dx-header-row
  > td {
  position: relative;
  padding: 8px 16px !important;
  border: none !important;
  background: transparent !important;
}

/* Borde entre columna 1 y 2 */
.custom-grid-detail-RiskAnalysis
  .dx-datagrid-headers
  .dx-header-row
  > td:nth-child(1)::after {
  content: '';
  position: absolute;
  right: 0; /* borde al final de la columna 1 */
  top: 0;
  height: 100%;
  width: 1.8px; /* grosor */
  background: #c2d8f0;
  pointer-events: none;
  opacity: 0.5;
}
/* SOLO primera fila de header: recorta altura */
.custom-grid-detail-RiskAnalysis
  .dx-datagrid-headers
  .dx-header-row:first-child
  > td:nth-child(1)::after {
  height: calc(100% - 8px); /* ajusta el “hueco” arriba */
  top: 8px; /* empuja hacia abajo solo en esa fila */
}

/* Borde entre columna 2 y 3 */
.custom-grid-detail-RiskAnalysis
  .dx-datagrid-headers
  .dx-header-row
  > td:nth-child(2)::after {
  content: '';
  position: absolute;
  right: 0; /* borde al final de la columna 2 */
  top: 0;
  height: 100%;
  width: 2px;
  background: #c2d8f0;
  pointer-events: none;
  opacity: 0.5;
}
/* SOLO primera fila de header: recorta altura */
.custom-grid-detail-RiskAnalysis
  .dx-datagrid-headers
  .dx-header-row:first-child
  > td:nth-child(2)::after {
  height: calc(100% - 8px); /* ajusta el “hueco” arriba */
  top: 8px; /* empuja hacia abajo solo en esa fila */
}

/* Quitar espacio extra bajo la toolbar */
.custom-grid-detail-RiskAnalysis .dx-toolbar {
  padding-bottom: 0 !important;
  margin-bottom: 0 !important;
  min-height: auto !important;
}

/* Ajustar contenedor interno */
.custom-grid-detail-RiskAnalysis .dx-toolbar-items-container {
  align-items: center;
  padding-bottom: 0 !important;
}

/* Quitar margen superior del header del grid */
.custom-grid-detail-RiskAnalysis .dx-datagrid-headers {
  margin-top: 0 !important;
}
/* Toolbar como fila única */
.custom-grid-detail-RiskAnalysis .dx-toolbar-items-container {
  display: flex !important;
  align-items: center !important;
}

/* Bloque izquierdo (filtros inline) */
.custom-grid-detail-RiskAnalysis .dx-toolbar-before {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1 1 auto; /* ⬅️ ocupa todo el espacio disponible */
}

/* Bloque derecho (buscar + excel) */
.custom-grid-detail-RiskAnalysis .dx-toolbar-after {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 16px;
  flex: 0 0 auto;
}

.custom-grid-detail-RiskAnalysis .dx-searchbox .dx-texteditor-input {
  padding-left: 36px !important;
  height: 40px !important;
  line-height: 40px !important;
}

/* Contenedor */
.custom-grid-detail-RiskAnalysis .dx-searchbox {
  position: relative;
  height: 40px !important;
}

/* ICONO */
.custom-grid-detail-RiskAnalysis .dx-searchbox::before {
  content: '';
  position: absolute;

  left: 7px;
  top: 45%;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;

  background-image: url('@/static/icons/ic_search_blue.svg');
  background-repeat: no-repeat;
  background-size: 24px 24px;

  pointer-events: none;
}
</style>
